import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

type CursorKind = 'link' | 'button' | 'gallery' | null;

const LABELS: Record<Exclude<CursorKind, null>, string> = {
  link: 'View more',
  button: 'Click',
  gallery: 'Open Gallery',
};

// Real text-entry fields keep the native I-beam — everything else (including
// checkboxes/radios/submit buttons, which behave like buttons) uses the dot.
const TEXT_FIELD_SELECTOR =
  'input:not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]';

/** Replaces the native cursor on desktop with a small circle that morphs
 * into a labeled pill over links, buttons, and gallery-opening photos.
 * Position is driven imperatively via requestAnimationFrame centered
 * exactly on the pointer (no re-render per mouse move); only the
 * label/visibility change triggers React state, which is what the CSS
 * transition on the pill animates. Falls back to the native cursor
 * entirely on touch devices and inside text fields. */
export default function CustomCursor() {
  const [enabled, setEnabled] = useState(false);
  const [kind, setKind] = useState<CursorKind>(null);
  const [overTextField, setOverTextField] = useState(false);
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const targetPos = useRef({ x: -100, y: -100 });
  const renderedPos = useRef({ x: -100, y: -100 });
  const rafRef = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const update = () => setEnabled(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    document.documentElement.classList.add('hs-custom-cursor-active');
    return () => document.documentElement.classList.remove('hs-custom-cursor-active');
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ease = reduceMotion ? 1 : 0.28;

    const resolveKind = (el: Element | null): CursorKind => {
      if (!el) return null;
      const explicit = el.closest('[data-cursor]') as HTMLElement | null;
      if (explicit) {
        const v = explicit.dataset.cursor;
        return v === 'link' || v === 'button' || v === 'gallery' ? v : null;
      }
      if (el.closest('button, [role="button"], input[type="submit"], input[type="button"]')) return 'button';
      if (el.closest('a[href]')) return 'link';
      return null;
    };

    const handleMove = (e: MouseEvent) => {
      targetPos.current = { x: e.clientX, y: e.clientY };
      setVisible(true);
      const el = e.target as Element;
      setOverTextField(!!el.closest(TEXT_FIELD_SELECTOR));
      setKind(resolveKind(el));
    };
    const handleLeaveWindow = () => setVisible(false);

    window.addEventListener('mousemove', handleMove, { passive: true });
    document.addEventListener('mouseleave', handleLeaveWindow);

    const tick = () => {
      renderedPos.current.x += (targetPos.current.x - renderedPos.current.x) * ease;
      renderedPos.current.y += (targetPos.current.y - renderedPos.current.y) * ease;
      if (wrapperRef.current) {
        wrapperRef.current.style.transform = `translate3d(${renderedPos.current.x}px, ${renderedPos.current.y}px, 0) translate(-50%, -50%)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseleave', handleLeaveWindow);
      cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  const show = visible && !overTextField;
  const label = kind ? LABELS[kind] : null;

  return (
    <div
      ref={wrapperRef}
      aria-hidden="true"
      className={cn(
        'fixed left-0 top-0 z-[300] pointer-events-none will-change-transform',
        'flex items-center justify-center rounded-full whitespace-nowrap font-semibold tracking-wide',
        'transition-[opacity,width,height,padding,background-color] duration-200 ease-out',
        show ? 'opacity-100' : 'opacity-0',
        label
          ? 'h-11 px-4 text-[12.5px] bg-olive text-paper mix-blend-normal shadow-[0_8px_20px_rgba(33,32,28,0.25)]'
          : 'h-2.5 w-2.5 bg-white mix-blend-difference',
      )}
    >
      {label}
    </div>
  );
}
